import { CameraChange, InputEventTypes, InputManager, ObjectType, PickingMode, PropertyType } from '@pvw/plmvisweb';
import { debounce } from 'lodash';

let controlManager = null;
let viewer = null;
let dataEventId = 'on-demand-data';
let PLMVisWeb = {
    InputManager: InputManager,
    InputEventTypes: InputEventTypes,
    CameraChange: CameraChange,
    PickingMode: PickingMode,
    PropertyType: PropertyType,
    ObjectType: ObjectType
};
let displayFetched = false;
let listeners = new Set();
let _camThreshold = 0;
let _mouseThreshold = 0;
let _debouncedCameraChange = debounce(cameraChanged, 50, { leading: false, trailing: true });

/**
 * Initialize On Demand Data module.  This call should only be made once, and after PLMVisWeb
 * has been initialized.  This module will periodically gather likely interaction elements
 * (currently just faces) and emit an 'on-demand-event' event to its listeners when complete.
 * It determines likely candidates from both the current view of the model in PLMVisWeb as
 * well as elements' proximity to the current mouse location.  If no listeners are registered,
 * this module will become inactive until a listener is registered.
 *
 * @function initialize
 * @type {Function}
 * @param {object} controlManagerInp - The PLMVisWeb control manager.  It is used to access the viewer
 *                                     dimensions, and to gain access to the Viewer object in use.
 * @param {number} cameraScreenThreshold - Any objects larger than this value in normalized screen space
 *                                         will be discovered if in view when the camera changes.  Normalized
 *                                         screen space ranges from -1 to 1 in both X and Y,
 *                                         so this value is approximately double the actual screen length used,
 *                                         depending on the window's aspect ratio.  Default is 0.18.
 * @param {number} mouseScreenThreshold - Any objects larger than this value in normalized screen space
 *                                         will be discovered when near the mouse.  Default is 0.06
 */
const initialize = (controlManagerInp, cameraScreenThreshold = 0.18, mouseScreenThreshold = 0.06) => {
    controlManager = controlManagerInp;
    _camThreshold = cameraScreenThreshold;
    _mouseThreshold = mouseScreenThreshold;
    viewer = controlManager.viewer;
    viewer.input.addEventListener(PLMVisWeb.InputEventTypes.Move, mouseMoveCb);
    viewer.registerCameraChangedEvent(_debouncedCameraChange, PLMVisWeb.CameraChange.POST);
    viewer.registerCameraChangedEvent(cameraChangedPre, PLMVisWeb.CameraChange.PRE);
    viewer.registerGeometryChangedEvent([ObjectType.BODY], onGeometryChanged);
};

/**
 * Register a listener with the On Demand Data module.
 *
 * @param {Function} listener - a listener to receive the on-demand-data event.  This event has
 *                              a candidateIds member, which is an array of psIds found by on-demand-data.
 *                              This listener will be called on all camera changes, and periodically during
 *                              mouse movement.  The candidateIds list will often contain members that were
 *                              sent in previous calls, so it is important for the listener to determine if
 *                              it already has the data needed prior to performing a server query for the
 *                              candidate's data.
 */
const addListener = (listener) => {
    if (!viewer) {
        throw new Error('on-demand-data module must be initialzied before use');
    }
    listeners.add(listener);
};

/**
 * Unregister a listener with the On Demand Data module.  It is important that this method be called
 * when appropriate so that this module can be disabled if does not have any active listeners.
 *
 * @param {Function} listener a listener to unregister.  This listener will no longer receive calls from
 *                            on-demand-data once removeListener is called.
 */
const removeListener = (listener) => {
    if (!viewer) {
        throw new Error('on-demand-data module must be initialzied before use');
    }
    listeners.delete(listener);
};

const onGeometryChanged = (changes) => {
    if (changes.Body) {
        let cleared = false;
        for (const [changeType, psIds] of Object.entries(changes.Body)) {
            for (let psId of psIds) {
                // only clear if it is not marked as a preview entity
                let props = viewer.properties.getProperties(psId, false, PropertyType.PlmProperties);
                if (!props || !props.properties || !props.properties.PreviewEntity) {
                    clearData();
                    cleared = true;
                    break;
                }
                if (cleared) break;
            }
        }
    }
};

class OnDemandDataEvent extends Event {
    constructor(candidateIds) {
        super(dataEventId);
        this.candidateIds = candidateIds;
    }
}

const cullFacesForFetch = (results, screenSize) => {
    let pixelSize = screenSize * getViewDiagonal();
    return viewer.cullIdsByRadius(results, pixelSize, 'PreviewEntity');
};

const getObjectIdsInArea = async (locMouseX, locMouseY, offsetX, offsetY, partialEnclosed, pickMask, visibleOnly) => {
    let promise = new Promise((resolve, reject) => {
        try {
            viewer.getObjectIdsInArea(
                locMouseX,
                locMouseY,
                offsetX,
                offsetY,
                (psIds) => {
                    // results is array of psId strings
                    resolve(psIds);
                },
                { partial: partialEnclosed, pickingMode: pickMask, visibleOnly: visibleOnly, scanPrecision: false }
            );
        } catch (error) {
            reject(error);
        }
    });
    return promise;
};

const collectFacesForFetch = async (minX, minY, maxX, maxY, screenSize) => {
    let results = [];

    try {
        results = await getObjectIdsInArea(minX, minY, maxX, maxY, true, PLMVisWeb.PickingMode.FACE, true);
    } catch (e) {
        results = [];
    }
    //let faceIds = await cullFacesForFetch(results, screenSize);

    let faceIds = [];
    let faceIdsPromise = [];
    let curMax = 0;
    let prevMax = 0;
    const pageSize = 20000;
    while (curMax < results.length) {
        if (!canCollect) {
            return [];
        }
        if (results.length - curMax < pageSize) {
            curMax = results.length;
        } else {
            curMax += pageSize;
        }
        let curFaceIdsPromise = callCullFacesForFetch(prevMax, curMax, results, screenSize);
        prevMax = curMax;
        faceIdsPromise.push(curFaceIdsPromise);
    }
    Promise.all(faceIdsPromise).then((faceIdsArr) => {
        faceIds = faceIdsArr.flat();
    });
    if (displayFetched) {
        viewer.setHighlightByPsId(faceIds, true, true);
    }
    return faceIds;
};

const callCullFacesForFetch = async (prevMax, curMax, results, screenSize) => {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            if (results.length === 0) {
                resolve([]);
                return;
            }
            try {
                let resSub = results.slice(prevMax, curMax);
                let faceIds = cullFacesForFetch(resSub, screenSize);
                resolve(faceIds);
            } catch (e) {
                reject(e);
            }
        }, 0);
    });
};
let canCollect = true;

const emitEvent = (oddEvent) => {
    for (let listener of listeners) {
        listener(oddEvent);
    }
};

var gridMap = new Map();

var LookUpCell = function (key) {
    return gridMap.get(key);
};

const getCellBoundary = (width, height, key) => {
    //which cell?
    let cellIdxStr = key.split('_', 2);
    let cellX = parseInt(cellIdxStr[0], 10);
    let cellY = parseInt(cellIdxStr[1], 10);
    //cell sizes?
    var cellsize_w = Math.round(width / 10);
    var cellsize_h = Math.round(height / 10);
    //minx, miny, maxx, maxy
    return [cellsize_w * cellX, cellsize_h * cellY, cellsize_w * (cellX + 1), cellsize_h * (cellY + 1)];
};
var ComputeCellKey = function (width, height, mouseLocation_x, mouselocation_y) {
    var cellsize_w = Math.round(width / 10);
    var cellsize_h = Math.round(height / 10);
    var x = Math.floor(mouseLocation_x / cellsize_w);
    var y = Math.floor(mouselocation_y / cellsize_h);
    var key = x.toString() + '_' + y.toString();
    return key;
};

var createCellElement = function (key, contents) {
    gridMap.set(key, contents);
};
let prevCell = '';
const shouldCollect = (e, key) => {
    if (!canCollect) {
        return false;
    }

    if (key === prevCell) {
        return false;
    }
    prevCell = key;

    return true;
};

/**
 *
 * @param e
 */
function mouseMoveCb(e) {
    //is anyone paying attention?
    if (listeners.size < 1 || !enabled) {
        return;
    }
    //are we really over PLMVisWeb?  Are they ever going to fix this?
    if (!e.currentTarget.nodeName || e.currentTarget.nodeName.toLowerCase() !== 'canvas') {
        return;
    }

    const controlSize = controlManager.getSize();

    var key = ComputeCellKey(controlSize.width, controlSize.height, e.offsetX, e.offsetY);
    // we only collect when crossing a cell boundary AND after staying in that boundary for
    // a short while (see delayCollect).  If we move to another cell prior to the timeout,
    // reset the timeout.  This avoids blitzing the server for info when the mouse is moving
    // from one side of screen to the other.
    if (shouldCollect(e, key)) {
        delayCollect.cancel();
        delayCollect(key, controlSize);
    }
}

const collectInCell = async (key, controlSize) => {
    //If we have results of a cell, no point in rediscovering.  We still fire
    //the event, in case something has changed for the listener
    let faceIds = LookUpCell(key);
    if (!faceIds) {
        let cellBoundary = getCellBoundary(controlSize.width, controlSize.height, key);

        faceIds = await collectFacesForFetch(cellBoundary[0], cellBoundary[1], cellBoundary[2], cellBoundary[3], _mouseThreshold);
        //add to cell
        createCellElement(key, faceIds);
    }
    if (faceIds && faceIds.length > 0) {
        //send event
        let oddEvent = new OnDemandDataEvent(faceIds);
        emitEvent(oddEvent);
    }
};

const delayCollect = debounce(collectInCell, 100);

const clearData = () => {
    gridMap.clear();
    prevCell = '';
};

const getViewDiagonal = () => {
    let controlSize = controlManager.getSize();
    return Math.sqrt(controlSize.width * controlSize.width + controlSize.height * controlSize.height);
};

const cameraChangedPre = () => {
    _debouncedCameraChange.cancel();
    canCollect = false;
};

/**
 *
 */
async function cameraChanged(/*data (contains camera info)*/) {
    clearData();
    canCollect = true;
    //is anyone paying attention?
    if (!canCollect || listeners.size < 1 || !enabled) {
        return;
    }
    let controlSize = controlManager.getSize();
    let faceIds = await collectFacesForFetch(0, 0, controlSize.width, controlSize.height, _camThreshold);
    if (faceIds && faceIds.length > 0) {
        //send event
        let oddEvent = new OnDemandDataEvent(faceIds);
        emitEvent(oddEvent);
    }
}

let enabled = true;
const enable = (enable = true) => {
    enabled = enable;
};

const isEnabled = () => enabled;

export default { initialize, addListener, removeListener, enable, isEnabled };
